import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, CardActionArea, Typography, Grid,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { faSpotify } from '@fortawesome/free-brands-svg-icons';
import { getImageSmallUrl } from '@sda/utils/image';
import { getRatingString } from '@sda/utils/string';
import { ProfileBadge } from '@client/components/common/ProfileBadge';
import { Clamp } from '@client/components/common/Clamp';
import { Media } from '@client/components/common/Media';
import { FontAwesomeIcon } from '@client/components/common/FontAwesomeIcon';
import type { ConnectCardProps } from '../../common/ConnectCard';

const useStyles = makeStyles({ name: 'ConnectCard' })((theme) => ({
  root: {
    borderRadius: theme.radii.large,
    marginBottom: theme.spacing(1.5),
    overflow: 'visible',
    position: 'relative',
    width: 245,
  },
  media: {
    borderTopLeftRadius: theme.radii.large,
    borderTopRightRadius: theme.radii.large,
    overflow: 'hidden',
  },
  mediaContainer: {
    boxShadow: theme.shadows[4],
    position: 'relative',
    width: '100%',
  },
  cardContent: {
    padding: theme.spacing(1.5),
  },
  titleContainer: {
    flex: 1,
  },
  favoriteLabel: {
    height: theme.spacing(2),

    '& > svg': {
      fontSize: 15,
      marginRight: theme.spacing(1 / 2),
      verticalAlign: 'middle',
    },

    '& > span': {
      verticalAlign: 'middle',
    },
  },
}));

export const ShowCallCard = forwardRef<HTMLDivElement, ConnectCardProps>(({
  imgSrc, path, title, numReviews,
  score, location, dateRange, badge,
  isFeatured, spotifyMonthlyActiveListeners,
}, ref) => {
  const { classes } = useStyles();

  return (
    <>
      <Card
        className={classes.root}
        role="presentation"
        ref={ref}
      >
        <CardActionArea
          component={Link}
          to={path}
          className={classes.mediaContainer}
        >
          {Boolean(imgSrc) && (
            <Media
              className={classes.media}
              images={imgSrc}
              sizeCallback={getImageSmallUrl}
              title={title}
              enableCarousel
            />
          )}

          {(Boolean(badge) || Boolean(isFeatured)) && (
            <ProfileBadge
              isFeatured={isFeatured}
              badgeId={badge}
            />
          )}

          <div className={classes.cardContent}>
            <Clamp
              variant="body2"
              lines={1}
            >
              {title}
            </Clamp>

            <Typography paragraph>
              {location}

              {Boolean(location) && ', '}

              {dateRange}
            </Typography>

            <Grid
              container
              spacing={1}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.favoriteLabel}
                >
                  <FavoriteIcon color="error" />

                  <span>
                    {getRatingString(score, numReviews)}
                  </span>
                </Typography>
              </Grid>

              {Boolean(spotifyMonthlyActiveListeners) && (
                <Grid item>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classes.favoriteLabel}
                  >
                    <FontAwesomeIcon
                      icon={faSpotify}
                      color="success"
                    />

                    <span>
                      {spotifyMonthlyActiveListeners}
                    </span>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </div>
        </CardActionArea>
      </Card>

      <div data-popper-arrow />
    </>
  );
});
